import { Box, Button, ButtonProps, Stack, styled, TextField } from '@mui/material'
import { DateTime } from 'luxon'
import { FC, useState } from 'react'
import { Camera, DeviceInfractionDetailsInfractionType } from '../../../../api/models'
import CloseIcon from '../../../../assets/images/close-icon.svg?react'
import { DateTimeRange } from './DateTimeRange'
import { DateRangeCalendar } from './DateRangeCalendar'
import { ActionType } from '../cameraOverviewEnums'
import { SpecificDatesCalendar } from './SpecificDatesPicker'
import { InputDisplayChip } from './InputDisplayChip'
import { LoggerName, namedLogger } from '../../../../utils/logger'
import { ALL_APPLET_NAMES } from '../../../../constants'

const logger = namedLogger(LoggerName.DocumentPanel)

interface DocumentPanelProps {
  locationName: string
  locationId: string
  camera: Camera
  isLoading?: boolean
  onClose: () => void
  onContinue: (camera: Camera, details: string, links: string[]) => void
  onActionChange?: (action: ActionType.Communication | ActionType.UnenrollCamera) => void
  onReasonChange?: (reasons: DeviceInfractionDetailsInfractionType) => void
  onSpecificDatesChange?: (dates: DateTime[]) => void
  onDateRangeChange?: (range: DateTimeRange) => void
}

const ActionReasonButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'active'
})<ButtonProps & { active?: boolean }>(({ active, disabled }) => ({
  fontSize: 'var(--size-s)',
  fontWeight: '700',
  maxHeight: '2.5rem',
  color: disabled ? 'var(--button-disabled-fg)' : active ? 'var(--button-primary-fg)' : 'var(--button-video-fg)',
  borderColor: disabled ? 'var(--button-disabled-fg)' : active ? 'var(--button-primary-fg)' : 'var(--button-video-fg)',
  borderRadius: 'var(--border-radius-l)',
  backgroundColor: disabled ? 'var(--button-disabled)' : active ? 'var(--button-primary)' : 'var(--button-video)',
  cursor: disabled ? 'not-allowed' : 'pointer'
}))

const DateToggleButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'active'
})<ButtonProps & { active?: boolean }>(({ active }) => ({
  fontSize: 'var(--size-s)',
  fontWeight: '700',
  maxHeight: '2.5rem',
  color: active ? 'var(--button-primary-fg)' : 'var(--button-video-fg)',
  borderColor: active ? 'var(--button-primary-fg)' : 'var(--button-video-fg)',
  borderRadius: 'var(--border-radius-l)',
  backgroundColor: active ? 'var(--button-primary)' : 'var(--button-video)',
  padding: 'var(--spacing-2xs)'
}))

const TextInput = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    background: 'var(--bg-page)',
    borderRadius: 'var(--border-radius-l)',
    '& fieldset': {
      border: `0.125rem solid var(--border-medium)`,
      borderRadius: 'var(--border-radius-l)'
    },
    '&:hover fieldset': {
      borderColor: `var(--border-medium)`
    },
    '&.Mui-focused fieldset': {
      borderColor: `var(--border-medium)`
    },
    '& input': {
      caretColor: 'var(--text-primary)',
      background: 'transparent'
    }
  }
})

export const DocumentPanel: FC<DocumentPanelProps> = ({
  locationName,
  locationId,
  camera,
  isLoading = false,
  onClose,
  onContinue,
  onActionChange,
  onReasonChange,
  onSpecificDatesChange,
  onDateRangeChange
}) => {
  logger.info(`Document Panel opened for locationId '${locationId}' and cameraId '${camera.uuid}'`, {
    context: {
      applet: ALL_APPLET_NAMES.MONITORING_MODERATION,
      locationId: locationId ?? 'unknown',
      cameraId: camera.uuid ?? 'unknown'
    }
  })

  const [activeAction, setActiveAction] = useState<ActionType.Communication | ActionType.UnenrollCamera | null>(null)
  const [activeReason, setActiveReason] = useState<DeviceInfractionDetailsInfractionType | null>(null)
  const [activeDateOption, setActiveDateOption] = useState<'dateRange' | 'specificDates'>('dateRange')
  const [selectedDates, setSelectedDates] = useState<DateTime[]>([])
  const [selectedRange, setSelectedRange] = useState<DateTimeRange>({ start: null, end: null })
  const [currentInputLinkValue, setCurrentInputLinkValue] = useState('')
  const [inputLinks, setInputLinks] = useState<string[]>([])
  const [details, setDetails] = useState<string>('')

  const detailsLength = details.length

  const dateRange = () => {
    return selectedRange.start && selectedRange.end
      ? `${selectedRange.start.toFormat('MM/dd/yyyy')} - ${selectedRange.end.toFormat('MM/dd/yyyy')}`
      : ''
  }

  const handleActiveDateOptionToggle = (value: 'dateRange' | 'specificDates') => {
    if (!value) return
    setActiveDateOption(value)
    if (value === 'dateRange') {
      setSelectedDates([])
    } else if (value === 'specificDates') {
      setSelectedRange({ start: null, end: null })
    }
  }

  const handleSetSelectedDates = (dates: DateTime[]) => {
    setSelectedRange({ start: null, end: null })
    setSelectedDates(dates)
    if (dates.length > 0) {
      onSpecificDatesChange?.(dates)
    }
  }

  const handleSetSelectedRange = (range: DateTimeRange) => {
    setSelectedDates([])
    setSelectedRange(range)
    if (range.start && range.end) {
      onDateRangeChange?.({ start: range.start, end: range.end })
    }
  }

  const shouldEnableContinueButton = () => {
    const actionWasSelected = !!activeAction
    const reasonWasSelected = !!activeReason
    const dateWasSelected = selectedDates.length > 0 || (selectedRange.start !== null && selectedRange.end !== null)
    const detailsWereAdded = detailsLength > 0
    return actionWasSelected && reasonWasSelected && dateWasSelected && detailsWereAdded
  }

  const handleContinue = () => {
    onContinue(camera, details, inputLinks)
  }

  const handleActionChange = (action: ActionType.Communication | ActionType.UnenrollCamera) => {
    setActiveAction(action)

    // If selecting "Warn customer" and "Gross misconduct" is selected, clear the reason
    if (action === ActionType.Communication && activeReason === DeviceInfractionDetailsInfractionType.GrossMisconduct) {
      setActiveReason(null)
    }

    if (onActionChange) {
      onActionChange(action)
    }
  }

  const handleReasonChange = (reason: DeviceInfractionDetailsInfractionType) => {
    setActiveReason(reason)

    // If selecting "Gross misconduct" and "Warn customer" is selected, clear the action
    if (reason === DeviceInfractionDetailsInfractionType.GrossMisconduct && activeAction === ActionType.Communication) {
      setActiveAction(null)
    }

    if (onReasonChange) {
      onReasonChange(reason)
    }
  }

  return (
    <Stack
      direction="column"
      data-testid="document-panel"
      sx={{ width: '25vw', minWidth: '25rem', background: 'var(--bg-surface)', overflowY: 'auto' }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          padding: 'var(--spacing-m) var(--spacing-m) var(--spacing-s) var(--spacing-m)'
        }}
      >
        <Box sx={{ fontSize: '1.5rem', fontWeight: '500', color: 'var(--text-primary)' }}>Document Panel</Box>
        <CloseIcon onClick={onClose} style={{ cursor: 'pointer' }} data-testid="document-panel-close-button" />
      </Stack>
      <Stack
        direction="column"
        sx={{ padding: '0 var(--spacing-m) var(--spacing-s) var(--spacing-m)', borderBottom: '0.0625rem solid var(--border-medium)' }}
      >
        <Box sx={{ fontWeight: '700', color: 'var(--text-primary)' }}>Customer Details</Box>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 'var(--spacing-s)', alignItems: 'center', padding: 'var(--spacing-s) 0 0 0' }}>
          <Stack direction="column" sx={{ gap: 'var(--spacing-2xs)' }}>
            <Box sx={{ fontSize: 'var(--size-xs)', fontWeight: '400', color: 'var(--text-secondary)' }}>Location</Box>
            <Box
              sx={{
                fontSize: 'var(--size-xs)',
                fontWeight: '400',
                padding: 'var(--spacing-2xs)',
                borderRadius: 'var(--border-radius-m)',
                background: 'var(--bg-page)',
                color: ''
              }}
            >
              {locationName}
            </Box>
          </Stack>
          <Stack direction="column" sx={{ gap: 'var(--spacing-2xs)' }}>
            <Box sx={{ fontSize: 'var(--size-xs)', fontWeight: '400', color: 'var(--text-secondary)' }}>SID</Box>
            <Box
              sx={{
                fontSize: 'var(--size-xs)',
                fontWeight: '400',
                padding: 'var(--spacing-2xs)',
                borderRadius: 'var(--border-radius-m)',
                background: 'var(--bg-page)'
              }}
            >
              {locationId}
            </Box>
          </Stack>
          <Stack direction="column" sx={{ gap: 'var(--spacing-2xs)' }}>
            <Box sx={{ fontSize: 'var(--size-xs)', fontWeight: '400', color: 'var(--text-secondary)' }}>Camera name</Box>
            <Box
              sx={{
                fontSize: 'var(--size-xs)',
                fontWeight: '400',
                padding: 'var(--spacing-2xs)',
                borderRadius: 'var(--border-radius-m)',
                background: 'var(--bg-page)'
              }}
            >
              {camera.name}
            </Box>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="column" sx={{ padding: 'var(--spacing-s) var(--spacing-m)', borderBottom: '0.0625rem solid var(--border-medium)' }}>
        <Box sx={{ fontWeight: '700', color: 'var(--text-primary)' }}>Actions</Box>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 'var(--spacing-s)', alignItems: 'center', padding: 'var(--spacing-s) 0 0 0' }}>
          <ActionReasonButton
            active={activeAction === ActionType.Communication}
            variant={activeAction === ActionType.Communication ? 'contained' : 'outlined'}
            disabled={activeReason === DeviceInfractionDetailsInfractionType.GrossMisconduct}
            onClick={() => handleActionChange(ActionType.Communication)}
          >
            Warn customer
          </ActionReasonButton>
          <ActionReasonButton
            active={activeAction === ActionType.UnenrollCamera}
            variant={activeAction === ActionType.UnenrollCamera ? 'contained' : 'outlined'}
            onClick={() => handleActionChange(ActionType.UnenrollCamera)}
          >
            Unenroll camera
          </ActionReasonButton>
        </Stack>
      </Stack>
      <Stack direction="column" sx={{ padding: 'var(--spacing-s) var(--spacing-m)', borderBottom: '0.0625rem solid var(--border-medium)' }}>
        <Box sx={{ fontWeight: '700', color: 'var(--text-primary)' }}>Reason</Box>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 'var(--spacing-s)', alignItems: 'center', padding: 'var(--spacing-s) 0 0 0' }}>
          <ActionReasonButton
            active={activeReason === DeviceInfractionDetailsInfractionType.ImproperDevicePlacement}
            variant={activeReason === DeviceInfractionDetailsInfractionType.ImproperDevicePlacement ? 'contained' : 'outlined'}
            onClick={() => handleReasonChange(DeviceInfractionDetailsInfractionType.ImproperDevicePlacement)}
          >
            Indoor placement
          </ActionReasonButton>
          <ActionReasonButton
            active={activeReason === DeviceInfractionDetailsInfractionType.InappropriateBehaviorWitnessed}
            variant={activeReason === DeviceInfractionDetailsInfractionType.InappropriateBehaviorWitnessed ? 'contained' : 'outlined'}
            onClick={() => handleReasonChange(DeviceInfractionDetailsInfractionType.InappropriateBehaviorWitnessed)}
          >
            Inappropriate behavior
          </ActionReasonButton>
          <ActionReasonButton
            active={activeReason === DeviceInfractionDetailsInfractionType.HighEngagementVolume}
            variant={activeReason === DeviceInfractionDetailsInfractionType.HighEngagementVolume ? 'contained' : 'outlined'}
            onClick={() => handleReasonChange(DeviceInfractionDetailsInfractionType.HighEngagementVolume)}
          >
            High volume
          </ActionReasonButton>
          <ActionReasonButton
            active={activeReason === DeviceInfractionDetailsInfractionType.GrossMisconduct}
            variant={activeReason === DeviceInfractionDetailsInfractionType.GrossMisconduct ? 'contained' : 'outlined'}
            disabled={activeAction === ActionType.Communication}
            onClick={() => handleReasonChange(DeviceInfractionDetailsInfractionType.GrossMisconduct)}
          >
            Gross misconduct
          </ActionReasonButton>
        </Stack>
      </Stack>
      <Stack
        direction="column"
        sx={{ gap: 'var(--spacing-s)', padding: 'var(--spacing-s) var(--spacing-m)', borderBottom: '0.0625rem solid var(--border-medium)' }}
      >
        <Box sx={{ fontWeight: '700', color: 'var(--text-primary)' }}>Date of incident</Box>
        <Stack
          direction="row"
          display="inline-flex"
          sx={{
            gap: 'var(--spacing-s)',
            width: 'fit-content',
            alignItems: 'center',
            padding: 'var(--spacing-2xs)',
            borderRadius: 'var(--border-radius-l)',
            background: 'var(--bg-page)'
          }}
        >
          <DateToggleButton
            active={activeDateOption === 'dateRange'}
            variant={activeDateOption === 'dateRange' ? 'contained' : 'text'}
            onClick={() => handleActiveDateOptionToggle('dateRange')}
          >
            Date ranges
          </DateToggleButton>
          <DateToggleButton
            active={activeDateOption === 'specificDates'}
            variant={activeDateOption === 'specificDates' ? 'contained' : 'text'}
            onClick={() => handleActiveDateOptionToggle('specificDates')}
          >
            Specific dates
          </DateToggleButton>
        </Stack>
        <Box sx={{ border: '0.0625rem solid var(--border-medium)', borderRadius: 'var(--border-radius-s)' }}>
          {activeDateOption === 'specificDates' ? (
            <SpecificDatesCalendar dates={selectedDates} setDates={handleSetSelectedDates} />
          ) : (
            <DateRangeCalendar
              range={[selectedRange.start, selectedRange.end]}
              setRange={dates => handleSetSelectedRange({ start: dates[0], end: dates[1] })}
            />
          )}
        </Box>
        <Stack direction="row" flexWrap="wrap" sx={{ justifyContent: 'flex-end', gap: 'var(--spacing-2xs) var(--spacing-s)' }}>
          {selectedDates.length > 0 &&
            selectedDates.map(date => (
              <InputDisplayChip
                key={date.toFormat('MM/dd/yyyy')}
                inputText={date.toFormat('MM/dd/yyyy')}
                onDelete={() => {
                  handleSetSelectedDates(selectedDates.filter(d => !d.hasSame(date, 'day')))
                }}
              />
            ))}
          {selectedRange.start && selectedRange.end && (
            <InputDisplayChip
              inputText={dateRange()}
              onDelete={() => {
                handleSetSelectedRange({ start: null, end: null })
              }}
            />
          )}
        </Stack>
      </Stack>
      <Stack
        direction="column"
        sx={{ gap: 'var(--spacing-s)', padding: 'var(--spacing-s) var(--spacing-m)', borderBottom: '0.0625rem solid var(--border-medium)' }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 'var(--spacing-s)' }}>
          <Box sx={{ fontWeight: '700', color: 'var(--text-primary)' }}>Supporting links</Box>
          <Box sx={{ fontSize: 'var(--size-xs)', fontWeight: '400', color: 'var(--text-secondary)' }}>(Optional)</Box>
        </Stack>
        <TextInput
          id="links"
          label={'Press enter to add link'}
          autoComplete={'off'}
          value={currentInputLinkValue}
          onChange={e => setCurrentInputLinkValue(e.target.value)}
          onKeyDown={e => {
            const trimmedLinkInput = currentInputLinkValue.trim()
            if (e.key === 'Enter' && trimmedLinkInput !== '') {
              setInputLinks([...inputLinks, trimmedLinkInput])
              setCurrentInputLinkValue('')
            }
          }}
        />
        {inputLinks.length > 0 && (
          <Stack direction="row" flexWrap="wrap" sx={{ gap: 'var(--spacing-2xs) var(--spacing-s)' }}>
            {inputLinks.map((link, idx) => (
              <InputDisplayChip key={idx} inputText={link} onDelete={() => setInputLinks(inputLinks.filter((_, i) => i !== idx))} />
            ))}
          </Stack>
        )}
      </Stack>
      <Stack
        direction="column"
        sx={{ gap: 'var(--spacing-s)', padding: 'var(--spacing-s) var(--spacing-m)', borderBottom: '0.0625rem solid var(--border-medium)' }}
      >
        <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', width: '100%', gap: 'var(--spacing-2xs)' }}>
          <Box sx={{ fontWeight: '700', color: 'var(--text-primary)' }}>Details</Box>
          <Box sx={{ fontSize: 'var(--size-xs)', fontWeight: '400', color: 'var(--text-secondary)' }}>{detailsLength} / 800</Box>
        </Stack>
        <TextInput
          id="details"
          placeholder={'This is for internal use only.'}
          autoComplete={'off'}
          multiline
          rows={4}
          slotProps={{
            htmlInput: {
              maxLength: 800
            }
          }}
          onChange={e => setDetails(e.target.value)}
        />
      </Stack>
      <Box sx={{ padding: 'var(--spacing-s)', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="large"
          disabled={!shouldEnableContinueButton()}
          onClick={handleContinue}
          loading={isLoading}
          loadingPosition="end"
          sx={{ width: '8.375rem', height: '3.5rem', fontSize: 'var(--size-s)' }}
        >
          {isLoading ? 'Sending...' : 'Continue'}
        </Button>
      </Box>
    </Stack>
  )
}
